import React from 'react'
import { X, ShoppingCart, ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useCartStore } from '../../stores/cartStore'
import { CartItem } from './CartItem'
import { CartSummary } from './CartSummary'
import { useMenuTheme } from '@/contexts/MenuThemeContext'

interface CartDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const { items, itemCount } = useCartStore()
  const { theme } = useMenuTheme()

  const handleCheckout = () => {
    onClose()
    navigate('/checkout')
  }

  if (!isOpen) return null

  const count = itemCount()

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className="fixed right-0 top-0 h-full w-full sm:w-96 shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col"
        style={{ backgroundColor: theme.cardBackground }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4"
          style={{ borderBottomColor: theme.borderColor, borderBottomWidth: '1px' }}
        >
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" style={{ color: theme.textSecondary }} />
            <h2 className="text-lg font-semibold" style={{ color: theme.textColor }}>
              Il tuo ordine
            </h2>
            {count > 0 && (
              <span
                className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full"
                style={{
                  backgroundColor: `${theme.primaryColor}20`,
                  color: theme.primaryColor
                }}
              >
                {count}
              </span>
            )}
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full transition-colors"
            style={{ backgroundColor: `${theme.textColor}0d` }}
            aria-label="Chiudi carrello"
          >
            <X className="w-5 h-5" style={{ color: theme.textSecondary }} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-4 text-center">
              <ShoppingCart className="w-16 h-16 mb-4" style={{ color: theme.mutedColor }} />
              <h3 className="text-lg font-medium mb-2" style={{ color: theme.textColor }}>
                Il carrello è vuoto
              </h3>
              <p className="text-sm" style={{ color: theme.textSecondary }}>
                Aggiungi prodotti dal menu per iniziare il tuo ordine
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-2">
              {items.map((item) => (
                <CartItem key={`${item.productId}-${item.customizations}`} item={item} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div
            className="p-4 space-y-4"
            style={{ borderTopColor: theme.borderColor, borderTopWidth: '1px' }}
          >
            <CartSummary />

            <button
              onClick={handleCheckout}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white font-medium rounded-lg transition-all"
              style={{
                background: theme.buttonGradient,
                boxShadow: `0 10px 25px -5px ${theme.shadowColor}`
              }}
            >
              <span>Procedi al checkout</span>
              <ChevronRight className="w-5 h-5" />
            </button>

            <button
              onClick={onClose}
              className="w-full px-6 py-2 text-sm transition-colors"
              style={{ color: theme.textSecondary }}
            >
              Continua a ordinare
            </button>
          </div>
        )}
      </div>
    </>
  )
}
